// 患者データの型定義
// 看護師データの型定義（簡易版 - Nurseインターフェースの必要なプロパティのみ）
export interface SimpleNurse {
  id: number;
  name: string;
  nickname: string;
  role_type: string;
  gender_type: string;
  can_assign?: boolean;
  birthday?: string;
  email?: string;
  resigned_at?: string | null;
  current_sign_in_at?: string | null;
  // 対話関連の追加情報
  latest_talk_status?: string;               // 最新の対話ステータス
  latest_talk_last_chat_send_at?: string;    // 最新の対話での最後の発言日時
  latest_talk_last_chat_message?: string;    // 最新の対話での最後のメッセージ
  latest_talk_room_id?: number;              // 最新の対話ルームID
  latest_talk_room_created_at?: string;      // 最新の対話ルーム作成日時
  latest_talk_is_force_end?: boolean;        // 最新の対話が強制終了されたかどうか
  in_active_talk?: boolean;                  // 対話中かどうか
  blocked_at?: string;                       // ブロック日時
}

export interface Patient {
  id: number;
  name: string;
  nickname: string;
  birthday: string;
  gender_type: string;
  prefecture_type: string;
  matter_of_concern: string | null;
  // 就労情報
  employment_type: string | null;
  work_type: string | null;
  // 家族関係
  family_relationship_type: string | null;
  // がん治療関連情報
  cancer_histories: CancerHistory[] | any;
  cancer_treatment_info: string | null;
  cancer_treatment_type_other: string | null;
  cancer_adviser_partner_type_other: string | null;
  cancer_treatment_care_partner_type_other: string | null;
  // その他疾患情報
  other_diseases_info: string | null;
  // 管理情報
  role_type: string;
  senior_memo: string | null;
  // 外部連携
  user_id: string;
  stripe_customer_ext_id: string | null;
  // user_authsテーブルから取得するフィールド
  email: string;
  resigned_at: string | null; // 退会日時
  current_sign_in_at: string | null; // 最終ログイン日時
  reset_password_sent_at?: string | null; // パスワードリセット送信日時
  profile_image_url: string | null; // プロフィール画像
  // 対話状態
  in_active_talk?: boolean; // 対話中かどうか
  is_test_account: boolean; // テストアカウントかどうか
  // 最新の対話ルーム情報（APIから追加）
  latest_talk_status?: string; // 最新の対話ステータス
  latest_talk_last_chat_send_at?: string; // 最新の対話での最後の発言日時
  latest_talk_last_chat_message?: string; // 最新の対話での最後のメッセージ内容
  latest_talk_room_id?: number; // 最新の対話ルームID
  latest_talk_is_force_end?: boolean; // 最新の対話が強制終了されたかどうか
  // ブロック情報
  blocked_nurses?: SimpleNurse[]; // この患者をブロックした看護師リスト
  blocked_patients?: {
    [nurse_id: string]: {
      nurse_id: number;
      created_at: string | null;
    }
  }; // ブロック情報 (看護師ID -> ブロック日時のマップ)
  // 関連する看護師情報
  related_nurses?: SimpleNurse[]; // 対話したことのある看護師リスト
  // 運営相談ルーム
  admin_talk_rooms?: any[]; // 運営相談ルーム一覧
  // 管理用
  created_at: string;
  updated_at: string;
  /** ユーザー認証情報のトークン (キー: client_id や client_id_refresh など) */
  tokens?: Record<string, { token: string; expiry: number; token_extras?: any }>;
  fcm_tokens?: Record<string, any>;
}

// 患者一覧のレスポンス型
export interface PatientListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Patient[];
}

// ロール種別
export const ROLE_TYPES = {
  MYSELF: '本人',
  FAMILY: '家族'
};
// 家族関係性
export const FAMILY_RELATIONSHIP_TYPES = {
  GRANDPARENTS: '祖父母',
  FATHER: '父親',
  MOTHER: '母親',
  STEPFATHER: '義父',
  STEPMOTHER: '義母',
  SPOUSE: '配偶者',
  CHILD: '子供',
  SIBLING: '兄弟・姉妹',
  GRANDCHILD: '孫',
  OTHER: 'その他'
};
// ロール種別ごとの色設定
export const ROLE_COLORS = {
  MYSELF: '#4CAF50', // 緑（本人）
  FAMILY: '#2196F3'  // 青（家族）
};
// ロール種別の選択肢
export const ROLE_TYPE_OPTIONS = [
  { value: 'MYSELF', label: '本人', color: ROLE_COLORS.MYSELF },
  { value: 'FAMILY', label: '家族', color: ROLE_COLORS.FAMILY }
];
// がん種類
export const CANCER_TYPES = {
  STOMACH_CANCER: '胃がん',
  ESOPHAGEAL_CANCER: '食道がん',
  COLORECTAL_CANCER: '大腸がん',
  BREAST_CANCER: '乳がん',
  UTERINE_CANCER: '子宮がん',
  OVARIAN_CANCER: '卵巣がん',
  LIVER_CANCER: '肝臓がん',
  PROSTATE_CANCER: '前立腺がん',
  LUNG_CANCER: '肺がん',
  BLOOD_CANCER: '血液がん',
  MALIGNANT_LYMPHOMA: '悪性リンパ腫',
  OTHER: 'その他',
  UNKNOWN: '不明'
};
// 治療ステータス
export const CANCER_TREATMENT_STATUSES = {
  WAITING: 'これから治療',
  DOING: '治療中',
  OBSERVING: '経過観察中',
  REMISSION: '寛解',
  UNKNOWN: '不明'
};
// 治療ステータスの色
export const CANCER_TREATMENT_STATUS_COLORS = {
  WAITING: '#ff9800', // オレンジ（これから治療）
  DOING: '#f44336', // 赤（治療中）
  OBSERVING: '#2196f3', // 青（経過観察中）
  REMISSION: '#4caf50', // 緑（寛解）
  UNKNOWN: '#757575' // グレー（不明）
};

// がん罹患歴の型定義
export interface CancerHistory {
  id?: number;
  cancer_type: keyof typeof CANCER_TYPES;
  cancer_treatment_status: keyof typeof CANCER_TREATMENT_STATUSES;
  year?: number;
  month?: number;
  hospital?: string;
  doctor?: string;
  stage?: string; // ステージ情報
  notes?: string;
  other?: string; // OTHER型の場合の詳細
}

// がん罹患歴データを安全に処理するヘルパー関数
export const getCancerHistoriesArray = (cancerHistories: any): CancerHistory[] => {
  if (!cancerHistories) {
    return [];
  }
  try {
    // すでに配列の場合
    if (Array.isArray(cancerHistories)) {
      return cancerHistories;
    }
    // 文字列の場合はJSONとしてパース
    if (typeof cancerHistories === 'string') {
      try {
        const parsed = JSON.parse(cancerHistories);
        return Array.isArray(parsed) ? parsed : [ parsed ];
      } catch (parseError) {
        return [];
      }
    }
    // オブジェクトの場合の特殊処理
    if (typeof cancerHistories === 'object') {
      // 特殊なケース: cancer_type プロパティを持つオブジェクト単体
      if ('cancer_type' in cancerHistories) {
        return [ cancerHistories as CancerHistory ];
      }
      // 通常のオブジェクト
      const values = Object.values(cancerHistories);
      // 各値がオブジェクトかどうか確認
      const result = values.filter(value =>
        value && typeof value === 'object' && 'cancer_type' in value
      ) as CancerHistory[];
      return result.length > 0 ? result : [];
    }
    return [];
  } catch (e) {
    return [];
  }
};
// がん種類の表示名を取得する関数
export const getCancerTypeName = (cancerType: string | undefined): string => {
  if (!cancerType) return '不明';
  return CANCER_TYPES[cancerType as keyof typeof CANCER_TYPES] || cancerType;
};
// 就労形態
export const WORK_TYPES = {
  EXECUTIVE: '経営者',
  OFFICE_WORKER: '会社員',
  PUBLIC_SERVANT: '公務員',
  TEMPORARY_WORKER: '派遣・契約社員',
  SPECIALIST: '専門家（医師・弁護士・会計士等）',
  LIBERAL_PROFESSION: '自由業',
  FREE_LANCE: 'フリーランス',
  MATERNITY_LEAVE: '産休・育休',
  STUDENT: '学生',
  DOMESTIC_HELPER: '家事手伝い',
  HOUSE_WIFE_HUSBAND: '専業主婦・主夫',
  NONE: '無職',
  OTHER: 'その他'
};

// 退会状態の定義
export enum ResignedStatus {
  ACTIVE_ONLY = 'false',    // 有効ユーザーのみ
  INCLUDE_RESIGNED = 'true', // 退会済みを含む
  RESIGNED_ONLY = 'only'     // 退会済みのみ
}

// 退会状態の表示名
export const RESIGNED_STATUS_DISPLAY = {
  [ResignedStatus.ACTIVE_ONLY]: '有効ユーザーのみ',
  [ResignedStatus.INCLUDE_RESIGNED]: '退会済みを含む',
  [ResignedStatus.RESIGNED_ONLY]: '退会済みのみ'
};
// 患者タイプの背景色とボーダー色
export const PATIENT_THEME = {
  // 本人（患者）テーマ
  MYSELF: {
    BACKGROUND: '#e8f5e9',      // 薄い緑
    BORDER: '#c8e6c9',          // 緑のボーダー
    DARK: '#4CAF50'             // 濃い緑 - テキストやアクセント用
  },
  // 家族テーマ
  FAMILY: {
    BACKGROUND: '#e3f2fd',      // 薄い青
    BORDER: '#bbdefb',          // 青のボーダー
    DARK: '#2196F3'             // 濃い青 - テキストやアクセント用
  },
  // リスト表示用テーマ
  LIST: {
    BACKGROUND: '#f0f8f0',      // 薄い緑（患者一覧などで統一的に使用）
    BORDER: '#c8e6c9',          // 薄い緑のボーダー
    DARK: '#4caf50'             // 濃い緑 - テキストやアクセント用
  }
};

// 対話状態の定義
export enum TalkStatus {
  ALL = '',          // すべて（未選択）
  IN_TALK = 'doing', // 対話中
  WAITING = 'waiting' // 待機中
}

// 対話状態の表示名
export const TALK_STATUS_DISPLAY = {
  [TalkStatus.ALL]: 'すべて',
  [TalkStatus.IN_TALK]: '対話中',
  [TalkStatus.WAITING]: '待機中'
};

// ブロック状態の定義
export enum BlockedStatus {
  ALL = '',          // すべて（未選択）
  BLOCKED = 'true',  // ブロック済み
  NOT_BLOCKED = 'false' // 非ブロック
}

// ブロック状態の表示名
export const BLOCKED_STATUS_DISPLAY = {
  [BlockedStatus.ALL]: 'すべて',
  [BlockedStatus.BLOCKED]: '拒否されてる',
  [BlockedStatus.NOT_BLOCKED]: '拒否されてない'
};

// テストアカウント状態
export enum TestAccountStatus {
  ALL = 'all',
  NORMAL = 'normal',
  TEST = 'test'
}

// テストアカウント状態の表示名
export const TEST_ACCOUNT_STATUS_DISPLAY = {
  [TestAccountStatus.ALL]: 'テストを含む',
  [TestAccountStatus.NORMAL]: '通常のみ',
  [TestAccountStatus.TEST]: 'テストのみ'
};

// 患者検索パラメータ
export interface PatientSearchParams {
  page?: number;
  page_size?: number;
  search?: string;
  email_search?: string; // メールアドレス専用検索パラメータ
  user_id?: string; // user_id検索パラメータ (開発者モードのみ)
  ordering?: string;
  role_type?: string | string[];
  include_resigned?: string; // 退会状態（選択式）
  resigned_only?: string; // 退会済みのみフラグ
  family_relationship_type?: string;
  talk_status?: string; // 対話状態でフィルタリング
  blocked_status?: string; // ブロック状態でフィルタリング
  conversation_count?: string; // 対話回数（等しい）
  conversation_count_gte?: string; // 対話回数（以上）
  conversation_count_lte?: string; // 対話回数（以下）
  conversation_count_operator?: string; // 対話回数の比較演算子（eq, gte, lte）
  created_at_start?: string; // 登録日範囲検索（開始日）
  created_at_end?: string; // 登録日範囲検索（終了日）
  is_test_account?: string; // テストアカウントフィルター（all, normal, test）
}
